import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Loader2, Printer, RefreshCw } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { BoulderTally } from '../../components/competition/BoulderTally';
import { LeadCardSummary } from '../../components/competition/LeadCardSummary';
import { HeaderActions } from '../layout/header-actions';
import { useCurrentLocation } from '../hooks/useCurrentLocation';
import { useStickySearchParam } from '../hooks/useStickySearchParam';
import { useGymAdmin } from '../context/gym-admin-context';
import { STATUS_VARIANT } from '../round-status';
import { TiebreaksPanel } from '../components/TiebreaksPanel';
import { TiebreakMark } from '../../components/competition/TiebreakMark';
import { rankingAttempts, tally } from '../../utils/round-score-summary';
import { standingsLayout, type StandingsSection } from '../../utils/category-standings';
import {
  categoryStandingsService,
  type CategoryStandings,
  type StandingEntry,
  type StandingsCategory,
} from '../../services/category-standings';

/**
 * The stage's standings, one tab per class — "Classifiche".
 *
 * **A class's turns are one contest**, so they are one table: everybody of the
 * class who climbed in the morning and in the afternoon, ranked together, with a
 * "Turno" column saying where each climbed. A round of the class that is not a
 * turn (a final) is a table of its own below it. Turns that cannot be compared —
 * a different number of blocks for the class, a boulder turn and a lead one, or
 * (under OPES) a block judged in one and not in the other — are said to be so
 * and shown one table each.
 *
 * **Why a page and not a switch on the round's board.** The board is where the
 * cards of *this* turn are written, by the desk and by the judges; rows from
 * another turn there would be read-only noise on a writing surface. This page is
 * read, and printed, and pinned up.
 *
 * Live: the people who reach the panel's rounds keep the score here, so the
 * server serves them every round whatever state it is in (the room waits for the
 * turns to be closed — see `Api::Competitions::StandingsController`). The places
 * are the server's, by the competition's regulation — the same call the round's
 * board makes, so a position never reads two ways — and this page computes none.
 *
 * **Tie-breaks are called and decided here** (issue #364, `TiebreaksPanel`),
 * under each table, by whoever runs the competition (`manage`): it is the only
 * page that sees a class across its turns, which is what a tie-break belongs to.
 */
export default function StandingsPage() {
  const { t, i18n } = useTranslation();
  const { placePath } = useGymAdmin();
  const { location: competition, loading: competitionLoading, slug } = useCurrentLocation();

  const [categories, setCategories] = useState<StandingsCategory[] | null>(null);
  const [listFailed, setListFailed] = useState(false);
  const [standings, setStandings] = useState<CategoryStandings | null>(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  /** Only the latest answer counts: a quick tab change must not be overwritten. */
  const request = useRef(0);

  // The open class is the URL, like the round's board — remembered per stage.
  const [activeTab, setActiveTab] = useStickySearchParam(`standings-${slug ?? ''}`, 'category');

  useEffect(() => {
    if (!slug) return;

    let cancelled = false;
    setListFailed(false);
    categoryStandingsService
      .list(slug)
      .then((answer) => {
        if (!cancelled) setCategories(answer.categories);
      })
      .catch(() => {
        if (!cancelled) setListFailed(true);
      });

    return () => {
      cancelled = true;
    };
  }, [slug]);

  // The open tab has to be one of the classes: a stale link would otherwise
  // leave the page pointed at nothing.
  useEffect(() => {
    if (!categories) return;
    if (categories.some((category) => category.id === activeTab)) return;
    setActiveTab(categories[0]?.id ?? '');
  }, [categories, activeTab, setActiveTab]);

  const open = categories?.find((category) => category.id === activeTab) ?? null;
  const openId = open?.id ?? null;

  /** The class's standings; `quiet` keeps the table on screen while it reloads. */
  const load = useCallback(
    async (competitionSlug: string, categoryId: string, quiet = false) => {
      const ticket = ++request.current;
      setLoading(true);
      if (!quiet) setStandings(null);
      setFailed(false);

      try {
        const answer = await categoryStandingsService.get(competitionSlug, categoryId);
        if (ticket === request.current) setStandings(answer);
      } catch {
        if (ticket === request.current) setFailed(true);
      } finally {
        if (ticket === request.current) setLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    if (slug && openId) load(slug, openId);
  }, [slug, openId, load]);

  const layout = useMemo(() => (standings ? standingsLayout(standings) : null), [standings]);

  /** Every athlete's name, by entry id: what a tie names its athletes by. */
  const names = useMemo(() => {
    const groups = [standings?.turns, ...(standings?.rounds ?? [])];
    return new Map(
      groups.flatMap((group) =>
        (group?.entries ?? []).map((entry) => [entry.id, entry.full_name ?? t('gymAdmin.standings.unnamed')] as const)
      )
    );
  }, [standings, t]);

  if (competitionLoading || (!categories && !listFailed)) {
    return <Skeleton className="h-96 w-full max-w-6xl" />;
  }
  if (!competition || !slug) return null;

  return (
    <div className="max-w-6xl space-y-6">
      <HeaderActions>
        <Button
          variant="outline"
          size="sm"
          className="gap-2"
          disabled={!openId || loading}
          onClick={() => openId && load(slug, openId, true)}
          data-testid="refresh-standings"
        >
          <RefreshCw className="size-4" aria-hidden />
          {t('gymAdmin.standings.refresh')}
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="gap-2"
          disabled={!layout || layout.sections.length === 0}
          onClick={() => window.print()}
          data-testid="print-standings"
        >
          <Printer className="size-4" aria-hidden />
          {t('gymAdmin.roundScores.print')}
        </Button>
      </HeaderActions>

      <Card>
        <CardHeader>
          <CardTitle>{t('gymAdmin.standings.title')}</CardTitle>
          <CardDescription>{t('gymAdmin.standings.description')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {listFailed ? (
            <p className="py-8 text-center text-sm text-muted-foreground">
              {t('gymAdmin.standings.loadError')}
            </p>
          ) : categories && categories.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground" data-testid="standings-no-categories">
              {t('gymAdmin.standings.noCategories')}
            </p>
          ) : (
            <>
              {/* One tab per class that climbs at least one round here. Wraps on
                  a phone rather than scrolling off the side. */}
              <Tabs value={activeTab} onValueChange={setActiveTab} className="print:hidden">
                <TabsList className="h-auto flex-wrap justify-start">
                  {categories?.map((category) => (
                    <TabsTrigger key={category.id} value={category.id} data-testid={`standings-tab-${category.id}`}>
                      {category.name}
                    </TabsTrigger>
                  ))}
                </TabsList>
              </Tabs>

              <div data-print-area className="space-y-8">
                {/* A sheet has to say what it is once it has left the screen. */}
                <div className="hidden print:block">
                  <h1 className="text-base font-semibold">
                    {competition.name} — {t('gymAdmin.standings.printTitle', { category: open?.name ?? '' })}
                  </h1>
                  <p className="text-xs">{new Date().toLocaleDateString(i18n.language)}</p>
                </div>

                {loading && !standings ? (
                  <div className="space-y-3" data-testid="standings-loading">
                    {[1, 2, 3].map((i) => (
                      <Skeleton key={i} className="h-10 w-full" />
                    ))}
                  </div>
                ) : failed ? (
                  <p className="py-8 text-center text-sm text-muted-foreground">
                    {t('gymAdmin.standings.loadError')}
                  </p>
                ) : layout && layout.sections.length === 0 ? (
                  <p className="py-8 text-center text-sm text-muted-foreground">
                    {t('gymAdmin.standings.noRounds')}
                  </p>
                ) : (
                  layout && (
                    <>
                      {/* Said once, above the turns shown apart. */}
                      {layout.incomparable && (
                        <div
                          className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200"
                          role="status"
                          data-testid="standings-incomparable"
                        >
                          <AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden />
                          <div>
                            <p className="font-medium">
                              {t(`gymAdmin.standings.incomparable.${layout.incomparable}.title`)}
                            </p>
                            <p>
                              {t(`gymAdmin.standings.incomparable.${layout.incomparable}.description`, {
                                blocks: layout.judgedMismatch.join(', '),
                              })}
                            </p>
                          </div>
                        </div>
                      )}

                      {layout.sections.map((section) => (
                        <div key={section.key} className="space-y-3">
                          <SectionTable
                            section={section}
                            judgedBlocks={layout.judgedBlocks}
                            roundPath={(id) => placePath(`/rounds/${id}`)}
                          />
                          {standings?.manage && openId && (
                            <TiebreaksPanel
                              section={section}
                              competitionSlug={slug}
                              categoryId={openId}
                              names={names}
                              onChange={setStandings}
                            />
                          )}
                        </div>
                      ))}
                    </>
                  )
                )}
                {loading && standings && (
                  <Loader2 className="size-4 animate-spin text-muted-foreground print:hidden" aria-hidden />
                )}
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * One table: the class's turns merged, one turn shown apart, or a round that is
 * not a turn. Its heading names the rounds (each a way to its board) and their
 * state, so a table that may still move says so.
 */
function SectionTable({
  section,
  judgedBlocks,
  roundPath,
}: {
  section: StandingsSection;
  /** Whether the regulation tells judged blocks apart (OPES). */
  judgedBlocks: boolean;
  roundPath: (roundId: string) => string;
}) {
  const { t } = useTranslation();
  const roundNames = new Map(section.rounds.map((round) => [round.id, round.name]));
  const isLead = section.roundType === 'Lead';
  const isBoulder = section.roundType === 'Boulder';

  const kindLabel =
    section.kind === 'turns'
      ? t('gymAdmin.standings.kinds.turns', { count: section.rounds.length })
      : t(`gymAdmin.standings.kinds.${section.kind}`);

  return (
    <section className="space-y-3" data-testid={`standings-section-${section.key}`}>
      <div className="space-y-1">
        <h3 className="flex flex-wrap items-center gap-2 font-semibold">
          {section.rounds.map((round, index) => (
            <span key={round.id} className="inline-flex items-center gap-2">
              {index > 0 && <span className="text-muted-foreground">+</span>}
              <Link to={roundPath(round.id)} className="hover:underline">
                {round.name}
              </Link>
              <Badge variant={STATUS_VARIANT[round.status]} className="font-normal print:hidden">
                {t(`gymAdmin.rounds.status.${round.status}`)}
              </Badge>
            </span>
          ))}
        </h3>
        <p className="text-xs text-muted-foreground">
          {kindLabel} · {t('gymAdmin.standings.athletes', { count: section.rows.length })}
        </p>
        {/* Which blocks a judge stood at: under OPES, what their tops are worth. */}
        {judgedBlocks && isBoulder && section.judgedNumbers.length > 0 && (
          <p className="text-xs text-muted-foreground" data-testid="standings-judged-blocks">
            {t('competitionRankings.judgedBlocks', { blocks: section.judgedNumbers.join(', ') })}
          </p>
        )}
        {section.mixedStyles && (
          <p className="text-xs text-muted-foreground" data-testid="standings-mixed-styles">
            {t('leadRounds.style.mixedNote')}
          </p>
        )}
      </div>

      {section.rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t('gymAdmin.standings.empty')}</p>
      ) : (
        <div className="overflow-x-auto rounded-lg border">
          <table className="w-full text-sm">
            <thead className="border-b bg-muted/50 text-xs text-muted-foreground">
              <tr>
                <th className="w-12 px-3 py-2 text-left font-medium">{t('gymAdmin.roundScores.rank')}</th>
                <th className="px-3 py-2 text-left font-medium">{t('gymAdmin.roundScores.athlete')}</th>
                <th className="px-3 py-2 text-left font-medium">{t('gymAdmin.members.fields.team')}</th>
                {section.showTurn && (
                  <th className="px-3 py-2 text-left font-medium">{t('gymAdmin.standings.turn')}</th>
                )}
                <th className="px-3 py-2 text-left font-medium">
                  {t(isLead ? 'leadRounds.card' : 'gymAdmin.roundScores.solved')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {section.rows.map(({ entry, rank }) => (
                <tr key={entry.id} data-testid="standings-row">
                  <td className="px-3 py-2 font-semibold tabular-nums" data-testid="standings-rank">
                    {rank != null ? (
                      <span className="inline-flex items-center gap-1.5">
                        {rank}
                        <TiebreakMark tiebreak={entry.tiebreak} />
                      </span>
                    ) : (
                      <span className="font-normal text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <span className="font-medium" data-testid="standings-name">
                      {entry.full_name ?? t('gymAdmin.standings.unnamed')}
                    </span>
                    {entry.also_climbed_in.length > 0 && (
                      <AlsoClimbedIn entry={entry} roundNames={roundNames} />
                    )}
                  </td>
                  <td className="px-3 py-2 text-muted-foreground">{entry.team_name ?? '—'}</td>
                  {section.showTurn && (
                    <td className="px-3 py-2" data-testid="standings-turn">
                      {entry.round_id ? (
                        roundNames.get(entry.round_id) ?? '—'
                      ) : entry.turn_to_choose ? (
                        <Badge
                          variant="outline"
                          className="border-amber-500 font-normal text-amber-700 dark:text-amber-300"
                        >
                          {t('gymAdmin.standings.turnToChoose')}
                        </Badge>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                  )}
                  <td className="px-3 py-2">
                    <CardsSummary
                      entry={entry}
                      isLead={isLead}
                      isBoulder={isBoulder}
                      judged={section.judged}
                      showJudged={judgedBlocks}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

/**
 * An athlete with no turn booked and results on more than one: the ranking took
 * the first turn's, and this says which other turn holds results to put
 * straight on its board.
 */
function AlsoClimbedIn({ entry, roundNames }: { entry: StandingEntry; roundNames: Map<string, string> }) {
  const { t } = useTranslation();
  const others = entry.also_climbed_in.map((id) => roundNames.get(id) ?? '—').join(', ');
  const counted = entry.round_id ? roundNames.get(entry.round_id) ?? '' : '';

  return (
    <p
      className="mt-0.5 flex items-center gap-1 text-xs text-amber-700 dark:text-amber-300 print:hidden"
      title={t('gymAdmin.standings.alsoClimbedInHelp', { turn: counted })}
      data-testid="standings-also-climbed-in"
    >
      <AlertTriangle className="size-3 shrink-0" aria-hidden />
      {t('gymAdmin.standings.alsoClimbedIn', { turns: others })}
    </p>
  );
}

/** The cards at a glance, as the round's board shows them. */
function CardsSummary({
  entry,
  isLead,
  isBoulder,
  judged,
  showJudged,
}: {
  entry: StandingEntry;
  isLead: boolean;
  isBoulder: boolean;
  judged: Set<string>;
  showJudged: boolean;
}) {
  if (entry.cards.length === 0) return <span className="text-muted-foreground">—</span>;

  if (isLead) {
    return (
      <span className="inline-flex flex-wrap items-center gap-x-3 gap-y-1 text-xs" data-testid="standings-lead">
        {entry.cards.map((card) => (
          <LeadCardSummary
            key={card.round_climb_id}
            holds={card.holds_count}
            timeSeconds={card.time_seconds}
            formattedTime={card.formatted_time}
            topped={card.topped}
            climbedAs={card.climbed_as}
            label={card.climb_name}
            className="text-xs"
          />
        ))}
      </span>
    );
  }

  if (!isBoulder) return <span className="text-muted-foreground">—</span>;

  return (
    <BoulderTally
      tally={tally(entry.cards, judged)}
      showJudged={showJudged}
      attempts={rankingAttempts(entry.score)}
      testId="standings-tally"
    />
  );
}
